import { isNativeMobilePlatform } from "../../platform/detect"
import { taskIsSleeping } from "../taskAttention"
import type { ActionEvent } from "../../types"
import type { CodeStore } from "../store"

const NOTIFICATION_CLEANUP_MS = 5 * 60 * 1000

export class NotificationManager {
    private activeNotifications = new Map<string, Notification>()

    constructor(private store: CodeStore) {
        this.init()
    }

    private init(): void {
        if (!isNativeMobilePlatform()) this.requestPermission()
        this.store.execution.onAfterEvent((taskId, _eventType, success) => {
            this.handleEvent(taskId, success)
        })
    }

    private async requestPermission(): Promise<void> {
        try {
            if (typeof Notification === "undefined") return
            if (Notification.permission === "default") {
                await Notification.requestPermission()
            }
        } catch (err) {
            console.error("[NotificationManager] Failed to request notification permission:", err)
        }
    }

    private getNotificationMessage(success: boolean, label?: string): string {
        if (!success) return label ? `${label} failed.` : "Task failed."
        return label ? `${label} complete.` : "Action complete."
    }

    private getActionLabel(taskId: string): string | undefined {
        const task = this.store.tasks.getTask(taskId)
        if (!task?.events.length) return undefined

        const lastEvent = task.events[task.events.length - 1]
        if (lastEvent.type === "action") {
            return (lastEvent as ActionEvent).source.userLabel
        }
        return undefined
    }

    private handleEvent(taskId: string, success: boolean): void {
        const task = this.store.tasks.getTask(taskId)
        if (!task || taskIsSleeping(task)) return

        const label = this.getActionLabel(taskId)
        const message = this.getNotificationMessage(success, label)

        this.sendNotification(taskId, task.repoId, task.title, message)
    }

    private sendNotification(taskId: string, workspaceId: string, taskTitle: string, message: string): void {
        try {
            if (isNativeMobilePlatform()) return
            if (typeof Notification === "undefined") return
            if (Notification.permission !== "granted") return
            if (typeof document !== "undefined" && document.hasFocus()) return

            const tag = `wildlark-task-${taskId}`
            const notification = new Notification(taskTitle, {
                body: message,
                tag,
            })

            // Store reference to prevent GC from collecting the notification
            // before the user clicks it (macOS/Electron drops onclick otherwise)
            this.activeNotifications.set(tag, notification)

            const cleanup = () => {
                this.activeNotifications.delete(tag)
            }

            notification.onclick = () => {
                cleanup()
                notification.close()
                window.focus()
                try {
                    this.store.config.navigateToTask(workspaceId, taskId)
                } catch (err) {
                    console.error("[NotificationManager] Failed to navigate:", err)
                }
            }

            notification.onclose = () => {
                cleanup()
            }

            setTimeout(cleanup, NOTIFICATION_CLEANUP_MS)
        } catch (err) {
            console.error("[NotificationManager] Failed to send notification:", err)
        }
    }
}
